import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Users, Hash, TrendingUp } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';

const EmptyState = ({ message }) => (
  <div className="text-center py-12 text-gray-400">
    <MessageSquare size={36} className="mx-auto mb-3 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

export default function MessagesAnalyticsPage({ timeRange = '30d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardAPI.getMessagesAnalytics(timeRange);
      if (res.success) setData(res.data);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [timeRange]);

  useEffect(() => { load(); }, [load]);

  const hasData = data && data.totalMessages > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <MessageSquare className="text-blue-600" size={24} />
          Messaging Analytics
        </h1>
        <p className="text-sm text-gray-500">Chat activity and conversation trends</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Messages', value: data?.totalMessages ?? 0, icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Chat Rooms', value: data?.totalRooms ?? 0, icon: Hash, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Active Rooms', value: data?.activeRooms ?? 0, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Unique Senders', value: data?.uniqueSenders ?? 0, icon: Users, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
            <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center mb-2`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>
              {loading ? '—' : value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Messages Per Day
          </h3>
          {loading ? (
            <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
          ) : !hasData ? (
            <EmptyState message="No messages recorded yet. Once users start chatting, activity will appear here." />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.perDay}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="messages" stroke="#3b82f6" fill="#bfdbfe" name="Messages" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            New Chat Rooms Created
          </h3>
          {loading ? (
            <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
          ) : !hasData ? (
            <EmptyState message="No chat rooms created in this period yet." />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.perDay}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="newRooms" fill="#8b5cf6" radius={[3,3,0,0]} name="New Rooms" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Info banner when empty */}
      {!loading && !hasData && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <strong>Note:</strong> The chat_rooms_messages table currently has 0 records.
          This will populate as users start messaging each other on the mobile app.
        </div>
      )}
    </div>
  );
}